/*───────────────────────────────────────────────────────────────────────────*\
  File        : app/meridian/MeridianPdfPreviewClient.tsx
  Description : Client-side Meridian publication PDF launcher. Binds the
                existing static publication-page buttons directly to the full
                publication PDFs so users can open/download them without a
                lead-capture step.
\*───────────────────────────────────────────────────────────────────────────*/

"use client"

import { useCallback, useEffect } from "react"
import { trackMeridianEvent } from "@/src/utils/analytics"

type WorkSlug =
  | "dependable-software"
  | "human-assisted-ai"
  | "the-confluent-method"
  | "the-halocline"

interface WorkPdfConfig {
  readonly title: string
  readonly fullPdfUrl: string
}

interface MeridianPdfPreviewClientProps {
  readonly workSlug: WorkSlug
}

const WORK_PDFS: Record<WorkSlug, WorkPdfConfig> = {
  "dependable-software": {
    title: "The Discipline of Dependable Software",
    fullPdfUrl:
      "https://raw.githubusercontent.com/rbcg-ai/engineering-publications/main/Discipline_of_Dependable_Software.pdf",
  },
  "human-assisted-ai": {
    title: "Human-Assisted AI",
    fullPdfUrl:
      "https://raw.githubusercontent.com/rbcg-ai/engineering-publications/main/Human-Assisted_AI.pdf",
  },
  "the-confluent-method": {
    title: "The Confluent Method",
    fullPdfUrl:
      "https://raw.githubusercontent.com/rbcg-ai/engineering-publications/main/The_Confluent_Method.pdf",
  },
  "the-halocline": {
    title: "The Halocline",
    fullPdfUrl:
      "https://raw.githubusercontent.com/rbcg-ai/engineering-publications/main/The%20Halocline.pdf",
  },
}

export default function MeridianPdfPreviewClient({
  workSlug,
}: MeridianPdfPreviewClientProps) {
  const config: WorkPdfConfig = WORK_PDFS[workSlug]

  const openFullPdf = useCallback((): void => {
    trackMeridianEvent("meridian_pdf_download_clicked", {
      workSlug,
      workTitle: config.title,
      pagePath: window.location.pathname,
    })

    window.open(config.fullPdfUrl, "_blank", "noopener,noreferrer")
  }, [config.fullPdfUrl, config.title, workSlug])

  useEffect((): (() => void) => {
    const triggers: NodeListOf<HTMLElement> = document.querySelectorAll<HTMLElement>(
      `[data-meridian-pdf-trigger="${workSlug}"]`,
    )

    const handleTriggerClick = (event: Event): void => {
      event.preventDefault()
      openFullPdf()
    }

    for (let index = 0; index < triggers.length; index += 1) {
      const trigger: HTMLElement = triggers[index]
      trigger.addEventListener("click", handleTriggerClick)
    }

    return (): void => {
      for (let index = 0; index < triggers.length; index += 1) {
        const trigger: HTMLElement = triggers[index]
        trigger.removeEventListener("click", handleTriggerClick)
      }
    }
  }, [openFullPdf, workSlug])

  return null
}

/*──────────────────────────── End of file ────────────────────────────────*/
